import { startTransition, useEffect, useMemo, useState } from "react";
import { LoaderCircle, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AffinityChartCanvas } from "@/components/affinity-chart/affinity-chart-canvas";
import { AffinityChartDetailPanel } from "@/components/affinity-chart/affinity-chart-detail-panel";
import { AffinityChartEmptyState } from "@/components/affinity-chart/affinity-chart-empty-state";
import { AffinityChartToolbar } from "@/components/affinity-chart/affinity-chart-toolbar";
import { AffinityChartUpgradePrompt } from "@/components/affinity-chart/affinity-chart-upgrade-prompt";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  deriveAffinityChartVisibleGraph,
  resolveAffinityChartDetail,
} from "@/features/affinity-chart/affinity-chart-elements";
import { useProjectShellStore } from "@/stores/projectShellStore";
import { useProjectAffinityChartStore } from "@/stores/projectAffinityChartStore";

const AFFINITY_DETAIL_PANEL_COLLAPSED_STORAGE_KEY = "affinityChart:detail-panel-collapsed";

function readAffinityDetailPanelCollapsedState() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(AFFINITY_DETAIL_PANEL_COLLAPSED_STORAGE_KEY) === "true";
}

export function ProjectAffinityChartPage() {
  const { t } = useTranslation();
  const currentProjectSummary = useProjectShellStore((state) => state.currentProjectSummary);
  const dashboardStatus = useProjectShellStore((state) => state.dashboardStatus);
  const access = useProjectAffinityChartStore((state) => state.access);
  const clearSelection = useProjectAffinityChartStore((state) => state.clearSelection);
  const errorMessage = useProjectAffinityChartStore((state) => state.errorMessage);
  const filters = useProjectAffinityChartStore((state) => state.filters);
  const initializeWorkspace = useProjectAffinityChartStore((state) => state.initializeWorkspace);
  const layoutRevision = useProjectAffinityChartStore((state) => state.layoutRevision);
  const notice = useProjectAffinityChartStore((state) => state.notice);
  const positions = useProjectAffinityChartStore((state) => state.positions);
  const refreshWorkspace = useProjectAffinityChartStore((state) => state.refreshWorkspace);
  const resetFilters = useProjectAffinityChartStore((state) => state.resetFilters);
  const resetLayout = useProjectAffinityChartStore((state) => state.resetLayout);
  const selection = useProjectAffinityChartStore((state) => state.selection);
  const selectEdge = useProjectAffinityChartStore((state) => state.selectEdge);
  const selectNode = useProjectAffinityChartStore((state) => state.selectNode);
  const setNotice = useProjectAffinityChartStore((state) => state.setNotice);
  const setPositions = useProjectAffinityChartStore((state) => state.setPositions);
  const snapshot = useProjectAffinityChartStore((state) => state.snapshot);
  const status = useProjectAffinityChartStore((state) => state.status);
  const toggleTypeFilter = useProjectAffinityChartStore((state) => state.toggleTypeFilter);
  const updateSavedPositions = useProjectAffinityChartStore((state) => state.updateSavedPositions);
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [focusRequestToken, setFocusRequestToken] = useState(0);
  const [detailPanelCollapsed, setDetailPanelCollapsed] = useState(() =>
    readAffinityDetailPanelCollapsedState(),
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      AFFINITY_DETAIL_PANEL_COLLAPSED_STORAGE_KEY,
      String(detailPanelCollapsed),
    );
  }, [detailPanelCollapsed]);

  useEffect(() => {
    if (!projectId || !currentProjectSummary || currentProjectSummary.projectId !== projectId) {
      return;
    }

    void initializeWorkspace(projectId, currentProjectSummary.capacity.tier);
  }, [currentProjectSummary, initializeWorkspace, projectId]);

  const visibleGraph = useMemo(
    () => (snapshot ? deriveAffinityChartVisibleGraph(snapshot, filters) : null),
    [filters, snapshot],
  );
  const detail = useMemo(
    () =>
      snapshot
        ? resolveAffinityChartDetail(snapshot, selection)
        : {
            descriptionKey: "affinityChart.detail.emptyBody",
            kind: "empty" as const,
            titleKey: "affinityChart.detail.emptyTitle",
          },
    [selection, snapshot],
  );

  if (dashboardStatus === "loading" && !currentProjectSummary) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 px-6 py-10 text-sm text-muted-foreground">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          {t("affinityChart.page.loading")}
        </CardContent>
      </Card>
    );
  }

  if (!projectId || !currentProjectSummary || currentProjectSummary.projectId !== projectId) {
    return null;
  }

  const graphableNodeCount = (snapshot?.counts.entityNodes ?? 0) + (snapshot?.counts.loreNodes ?? 0);

  return (
    <div className="space-y-6">
      {notice && notice.kind === "error" ? (
        <Card className={notice.kind === "error" ? "border-destructive/30" : undefined}>
          <CardContent
            aria-live={notice.kind === "error" ? "assertive" : "polite"}
            className="flex items-start justify-between gap-4 px-6 py-5 text-sm"
            role={notice.kind === "error" ? "alert" : "status"}
          >
            <div className="flex items-start gap-3">
              {notice.kind === "error" ? (
                <TriangleAlert className="mt-0.5 h-4 w-4 text-destructive" />
              ) : null}
              <p>{t(notice.message)}</p>
            </div>
            <Button onClick={() => setNotice(null)} type="button" variant="ghost">
              {t("projects.actions.dismiss")}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {status === "loading" ? (
        <Card>
          <CardContent className="flex items-center gap-3 px-6 py-10 text-sm text-muted-foreground">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            {t("affinityChart.page.preparing")}
          </CardContent>
        </Card>
      ) : null}

      {status === "error" ? (
        <Card>
          <CardContent className="space-y-4 px-6 py-8">
            <h2 className="text-2xl text-foreground">{t("affinityChart.page.errorTitle")}</h2>
            <p className="text-muted-foreground">
              {errorMessage ? t(errorMessage) : t("affinityChart.messages.loadError")}
            </p>
            <Button
              onClick={() => void refreshWorkspace(projectId, currentProjectSummary.capacity.tier)}
              type="button"
              variant="outline"
            >
              {t("affinityChart.actions.refresh")}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {access.state === "locked" ? (
        <AffinityChartUpgradePrompt
          onOpenEntities={() => {
            startTransition(() => {
              navigate(`/projects/${projectId}/entities`);
            });
          }}
          onOpenLore={() => {
            startTransition(() => {
              navigate(`/projects/${projectId}/lore`);
            });
          }}
          recommendedTier={access.recommendedTier}
        />
      ) : null}

      {status === "ready" && access.state === "available" && snapshot && visibleGraph ? (
        <div className="space-y-6">
          <AffinityChartToolbar
            filterOptions={snapshot.filterOptions}
            filters={filters}
            onRefresh={() => void refreshWorkspace(projectId, currentProjectSummary.capacity.tier)}
            onResetFilters={resetFilters}
            onResetLayout={() => void resetLayout()}
            onToggleType={toggleTypeFilter}
            snapshot={snapshot}
            visibleGraph={visibleGraph}
          />

          <div
            className={
              detailPanelCollapsed
                ? "grid gap-6 xl:grid-cols-[minmax(0,1fr)_2.75rem]"
                : "grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]"
            }
          >
            <div className="space-y-6">
              {graphableNodeCount === 0 ? (
                <AffinityChartEmptyState
                  actions={
                    <>
                      <Button
                        onClick={() => {
                          startTransition(() => {
                            navigate(`/projects/${projectId}/entities`);
                          });
                        }}
                        type="button"
                      >
                        {t("affinityChart.actions.openEntities")}
                      </Button>
                      <Button
                        onClick={() => {
                          startTransition(() => {
                            navigate(`/projects/${projectId}/lore`);
                          });
                        }}
                        type="button"
                        variant="outline"
                      >
                        {t("affinityChart.actions.openLore")}
                      </Button>
                    </>
                  }
                  description={t("affinityChart.empty.noRecordsBody")}
                  eyebrow={t("affinityChart.empty.noRecordsEyebrow")}
                  title={t("affinityChart.empty.noRecordsTitle")}
                />
              ) : visibleGraph.counts.visibleNodes === 0 ? (
                <AffinityChartEmptyState
                  actions={
                    <Button onClick={resetFilters} type="button" variant="outline">
                      {t("affinityChart.actions.clearFilters")}
                    </Button>
                  }
                  description={t("affinityChart.empty.filteredBody")}
                  eyebrow={t("affinityChart.empty.filteredEyebrow")}
                  title={t("affinityChart.empty.filteredTitle")}
                />
              ) : (
                <AffinityChartCanvas
                  edges={visibleGraph.edges}
                  focusRequestToken={focusRequestToken}
                  layoutRevision={layoutRevision}
                  nodes={visibleGraph.nodes}
                  onBackgroundSelect={clearSelection}
                  onEdgeSelect={selectEdge}
                  onNodeSelect={selectNode}
                  onPositionsChange={setPositions}
                  onPositionsCommit={(nextPositions) => void updateSavedPositions(nextPositions)}
                  positions={positions}
                  selection={selection}
                />
              )}
            </div>

            <AffinityChartDetailPanel
              collapsed={detailPanelCollapsed}
              detail={detail}
              onClearSelection={clearSelection}
              onOpenRecord={(href) => {
                startTransition(() => {
                  navigate(href);
                });
              }}
              onReturnFocus={() => setFocusRequestToken((token) => token + 1)}
              onToggleCollapsed={() => setDetailPanelCollapsed((current) => !current)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
